/**
 * Shared WCAG colour-contrast utilities.
 *
 * These helpers are used by useRestaurantTemplate (and any future hook or
 * component that needs to validate restaurant-supplied brand colours) to
 * ensure that accent colours meet the WCAG AA minimum contrast ratio before
 * they are applied to the page.
 */

/**
 * Converts a single sRGB channel value (0–255) to its linearised form.
 * @param {number} val - Channel value in the range [0, 255].
 * @returns {number} Linearised channel value.
 */
function linearise(val) {
  const s = val / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/**
 * Computes the WCAG relative luminance of a hex colour string.
 * Returns null when the string cannot be parsed as a 3- or 6-digit hex colour.
 *
 * @param {string} hex - Colour string such as "#RRGGBB" or "#RGB".
 * @returns {number|null} Relative luminance in [0, 1], or null on parse failure.
 */
export function hexToRelativeLuminance(hex) {
  if (typeof hex !== 'string') return null;
  const clean = hex.replace(/^#/, '');
  if (!/^[0-9A-Fa-f]+$/.test(clean)) return null;
  let r;
  let g;
  let b;
  if (clean.length === 3) {
    r = parseInt(clean[0] + clean[0], 16);
    g = parseInt(clean[1] + clean[1], 16);
    b = parseInt(clean[2] + clean[2], 16);
  } else if (clean.length === 6) {
    r = parseInt(clean.slice(0, 2), 16);
    g = parseInt(clean.slice(2, 4), 16);
    b = parseInt(clean.slice(4, 6), 16);
  } else {
    return null;
  }
  if ([r, g, b].some(Number.isNaN)) return null;
  return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}

/**
 * Returns the WCAG contrast ratio between two hex colours.
 * Returns null when either colour cannot be parsed.
 *
 * @param {string} hex1
 * @param {string} hex2
 * @returns {number|null} Contrast ratio (always ≥ 1), or null on parse failure.
 */
export function contrastRatio(hex1, hex2) {
  const l1 = hexToRelativeLuminance(hex1);
  const l2 = hexToRelativeLuminance(hex2);
  if (l1 === null || l2 === null) return null;
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Applies a WCAG contrast guard to a template token set that uses a
 * restaurant-supplied brand colour as its `primary` accent.
 *
 * The guard compares the accent against the template's background colour
 * (defaulting to white).  If the contrast ratio is below 3:1, `primaryText`
 * is switched to `#000000` so that overlaid text remains readable.  The
 * accent colour itself is preserved so the brand identity is not altered.
 *
 * This is the single authoritative implementation of the guard.  Every
 * template that lets restaurants override their accent colour should call
 * this helper rather than duplicating the comparison inline.
 *
 * @param {Object} tokens - The template token object (spread from the base template).
 * @param {string} accent - The restaurant-supplied accent hex colour.
 * @param {string} [background='#FFFFFF'] - The background colour to test against.
 * @returns {Object} Updated token object with `primary` set to `accent` and
 *   `primaryText` adjusted if necessary.
 */
export function applyBrandColourGuard(tokens, accent, background = '#FFFFFF') {
  const ratio = contrastRatio(accent, background);
  const primaryText =
    ratio !== null && ratio < 3 ? '#000000' : tokens.primaryText;
  return { ...tokens, primary: accent, primaryText };
}
